import { useMutation, useQuery } from '@apollo/client';
import { useTranslation } from 'react-i18next';

import {
  GetCompanyQuery,
  GetUserQuery,
  UpdateCompanyMutation,
  UpdateCompanyMutationVariables,
} from '@/modules/common/types';
import { UPDATE_COMPANY } from '@/services/graphql/mutations';
import { GET_COMPANY, GET_USER } from '@/services/graphql/queries';

import { useMessage } from './context/message';

const useCompany = (skipMessages?: boolean) => {
  const { setErrorMessage, setSuccessMessage } = useMessage();
  const { t } = useTranslation();

  const { loading, data } = useQuery<GetCompanyQuery>(GET_COMPANY, {
    onError: () => {
      setErrorMessage(t('messages.generic.error'));
    },
  });

  const [updateCompany, { loading: updateCompanyLoading }] = useMutation<
    UpdateCompanyMutation,
    UpdateCompanyMutationVariables
  >(UPDATE_COMPANY, {
    onError: () => {
      if (!skipMessages) {
        setErrorMessage(t('messages.updateCompany.error'));
      }
    },
    onCompleted: async () => {
      if (!skipMessages) {
        setSuccessMessage(t('messages.updateCompany.success'));
      }
    },
    fetchPolicy: 'no-cache',
    update(cache, { data }, { variables }) {
      const updatedCompany = data?.updateCompany;
      const { user, company } = cache.readQuery<GetUserQuery>({
        query: GET_USER,
      }) || { user: undefined, company: undefined };

      if (company) {
        cache.writeQuery({
          query: GET_USER,
          data: {
            user,
            company: {
              ...company,
            },
          },
        });
      }

      if (variables && variables.input.subIndustryId) {
        cache.modify({
          id: cache.identify({ id: updatedCompany?.id, __typename: 'Company' }),
          fields: {
            subIndustry: () => updatedCompany?.subIndustry,
            industry: () => updatedCompany?.industry,
          },
        });
      }
    },
  });

  return {
    loading,
    company: data?.company,
    updateCompany,
    updateCompanyLoading,
  };
};

export default useCompany;
